// What's new — right-side panel listing product updates, newest first.
//
// Opened from the Help menu (HelpMenu.tsx). Opening it records "seen"
// on the server, which clears the dot on the Help button; the items
// that were new when the panel opened keep their "New" mark until it
// closes, so the reader can still tell which ones they hadn't read.
//
// Accessibility: role="dialog" + aria-modal, labelled by its heading;
// focus moves to the close button on open, Tab is held inside the
// panel, Esc closes, and focus returns to the opener on close.
//
// Floating-layer style: white with a slate border, soft shadow and
// faint ring, with a dark-mode pair for every colour.

import { useEffect, useId, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ExternalLink, Loader2, Megaphone, X } from 'lucide-react';
import { useProductUpdates, type ProductUpdateItem } from '../../lib/productUpdates';
import type { AppKey } from '../../lib/apps';

const FLOATING_PANEL =
  'border-slate-200 bg-white shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900';

/** App tag label + tint (the suite's cool-family app colours). */
const APP_TAG: Record<AppKey, { label: string; className: string }> = {
  aware: { label: 'Aware', className: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:ring-violet-800' },
  xes: { label: 'XES', className: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800' },
  posture: { label: 'Posture', className: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:ring-blue-800' },
  surface: { label: 'Surface', className: 'bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-indigo-800' },
  exposure: { label: 'Exposure', className: 'bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-950/50 dark:text-teal-300 dark:ring-teal-800' },
  agents: { label: 'Agents', className: 'bg-purple-50 text-purple-700 ring-purple-200 dark:bg-purple-950/50 dark:text-purple-300 dark:ring-purple-800' },
  orchestrate: { label: 'Orchestrate', className: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/50 dark:text-cyan-300 dark:ring-cyan-800' },
  insider: { label: 'Insider', className: 'bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-950/50 dark:text-fuchsia-300 dark:ring-fuchsia-800' },
  resilience: { label: 'Resilience', className: 'bg-slate-100 text-slate-700 ring-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600' },
  'ai-gateway': { label: 'AI Gateway', className: 'bg-pink-50 text-pink-700 ring-pink-200 dark:bg-pink-950/50 dark:text-pink-300 dark:ring-pink-800' },
  'ai-security': { label: 'AI Security', className: 'bg-stone-100 text-stone-700 ring-stone-300 dark:bg-stone-800 dark:text-stone-200 dark:ring-stone-600' },
  umbra: { label: 'Umbra', className: 'bg-zinc-100 text-zinc-700 ring-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-zinc-600' },
};

const ALL_APPS_TAG = 'bg-slate-50 text-slate-600 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:ring-slate-700';

const TAG_BASE = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function AppTags({ apps }: { apps: AppKey[] }) {
  if (apps.length === 0) return <span className={`${TAG_BASE} ${ALL_APPS_TAG}`}>All apps</span>;
  return (
    <>
      {apps.map((key) => {
        const tag = APP_TAG[key];
        // An app this build doesn't know yet still shows, untinted.
        return (
          <span key={key} className={`${TAG_BASE} ${tag?.className ?? ALL_APPS_TAG}`}>
            {tag?.label ?? key}
          </span>
        );
      })}
    </>
  );
}

function UpdateEntry({ update, isNew }: { update: ProductUpdateItem; isNew: boolean }) {
  const headingId = useId();
  return (
    <article aria-labelledby={headingId} className="border-b border-slate-200 px-5 py-4 last:border-b-0 dark:border-slate-700">
      <div className="flex flex-wrap items-center gap-1.5">
        {isNew && (
          <span className="inline-flex items-center rounded-full bg-indigo-600 px-2 py-0.5 text-[11px] font-semibold text-white dark:bg-indigo-500">
            New
          </span>
        )}
        <AppTags apps={update.apps} />
        <time dateTime={new Date(update.publishedAt).toISOString()} className="ml-auto text-xs text-slate-500 dark:text-slate-400">
          {formatDate(update.publishedAt)}
        </time>
      </div>
      <h3 id={headingId} className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
        {update.title}
      </h3>
      <div className="prose prose-sm mt-1 max-w-none text-slate-700 dark:prose-invert dark:text-slate-300 prose-p:my-1.5 prose-ul:my-1.5 prose-code:before:content-none prose-code:after:content-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) =>
              href ? (
                <a href={href} target="_blank" rel="noreferrer noopener">
                  {children}
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : (
                <span>{children}</span>
              ),
          }}
        >
          {update.body}
        </ReactMarkdown>
      </div>
      {update.link && (
        <a
          href={update.link}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-2 inline-flex items-center gap-1 rounded text-sm font-medium text-indigo-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-indigo-300"
        >
          Learn more
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">about {update.title} (opens in a new tab)</span>
        </a>
      )}
    </article>
  );
}

export interface WhatsNewPanelProps {
  open: boolean;
  onClose: () => void;
  /** Where focus goes when the panel closes. Defaults to whatever had
   *  focus when it opened. */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function WhatsNewPanel({ open, onClose, returnFocusRef }: WhatsNewPanelProps) {
  const { updates, loading, error, markSeen } = useProductUpdates();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  // Ids that were unseen when the panel opened; they keep "New" until close.
  const [newIds, setNewIds] = useState<ReadonlySet<string>>(new Set());
  const [seenRecorded, setSeenRecorded] = useState(false);

  // Open: remember the opener and focus the close button.
  // Close: hand focus back.
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      openerRef.current = document.activeElement as HTMLElement | null;
      setSeenRecorded(false);
      const raf = requestAnimationFrame(() => closeRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
    // Nothing to hand back on first mount (closed from the start).
    if (!wasOpenRef.current) return undefined;
    wasOpenRef.current = false;
    const back = returnFocusRef?.current ?? openerRef.current;
    openerRef.current = null;
    setNewIds(new Set());
    if (back && typeof back.focus === 'function') back.focus();
    return undefined;
  }, [open, returnFocusRef]);

  // Once the feed is in hand, note what was new and record "seen".
  useEffect(() => {
    if (!open || seenRecorded || loading) return;
    setSeenRecorded(true);
    setNewIds(new Set(updates.filter((u) => u.unseen).map((u) => u.id)));
    void markSeen();
  }, [open, seenRecorded, loading, updates, markSeen]);

  if (!open) return null;

  const onKeyDown = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape') {
      ev.preventDefault();
      onClose();
      return;
    }
    if (ev.key !== 'Tab' || !panelRef.current) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" onKeyDown={onKeyDown}>
      <div className="absolute inset-0 bg-slate-900/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex h-full w-full max-w-md flex-col border-l text-slate-900 dark:text-slate-100 ${FLOATING_PANEL}`}
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
          <Megaphone className="h-4 w-4 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
          <h2 id={titleId} className="text-base font-semibold">
            What’s new
          </h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close what’s new"
            className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" aria-busy={loading}>
          {loading && updates.length === 0 ? (
            <p className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Loading updates…
            </p>
          ) : error && updates.length === 0 ? (
            <p role="alert" className="px-5 py-6 text-sm text-slate-700 dark:text-slate-300">
              We couldn’t load updates right now. {error}
            </p>
          ) : updates.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-500 dark:text-slate-400">No updates yet.</p>
          ) : (
            <>
              {error && (
                <p role="alert" className="border-b border-slate-200 px-5 py-2 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-400">
                  We couldn’t mark these updates as read, so they may show as new again. {error}
                </p>
              )}
              {updates.map((u) => (
                <UpdateEntry key={u.id} update={u} isNew={newIds.has(u.id)} />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
